﻿import { useState, useMemo } from "react";
import type { AssessmentQuestion, ModuleType, Answer } from "../types";
import { MODULE_CONFIG, MODULE_ORDER } from "../data/constants";

const ALL_MODULES: ModuleType[] = [...MODULE_ORDER, "V"];

export function Questionnaire({
  questions,
  onSubmit,
}: {
  questions: AssessmentQuestion[];
  onSubmit: (answers: Answer[]) => void;
}) {
  const [currentModIndex, setCurrentModIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string[]>>({});
  const [textAnswers, setTextAnswers] = useState<Record<number, string>>({});

  const currentMod = ALL_MODULES[currentModIndex];
  const modQuestions = useMemo(
    () => questions.filter((q) => q.module === currentMod),
    [questions, currentMod]
  );
  const config = MODULE_CONFIG[currentMod];
  const totalMods = ALL_MODULES.length;

  const isModuleComplete = modQuestions.every((q) => {
    if (q.input_type === "text") return true;
    const ans = answers[q.id];
    return ans && ans.length > 0;
  });

  const isModuleRequired = currentMod === "A";

  const handleSelect = (questionId: number, option: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: [option] }));
  };

  const handleMultiToggle = (questionId: number, option: string) => {
    setAnswers((prev) => {
      const existing = prev[questionId] || [];
      if (existing.includes(option)) {
        return { ...prev, [questionId]: existing.filter((o) => o !== option) };
      }
      return { ...prev, [questionId]: [...existing, option] };
    });
  };

  const handleTextChange = (questionId: number, text: string) => {
    setTextAnswers((prev) => ({ ...prev, [questionId]: text }));
  };

  const handleNext = () => {
    setCurrentModIndex((i) => Math.min(i + 1, totalMods - 1));
  };

  const handlePrev = () => {
    setCurrentModIndex((i) => Math.max(i - 1, 0));
  };

  const handleSubmit = () => {
    const allAnswers: Answer[] = questions
      .filter((q) => {
        if (q.input_type === "text") return textAnswers[q.id] && textAnswers[q.id].trim();
        return answers[q.id] && answers[q.id].length > 0;
      })
      .map((q) => {
        const base: Answer = {
          question_id: q.id,
          selected_options: q.input_type === "text" ? [] : (answers[q.id] || []),
        };
        if (q.question_number === "OE1") {
          base.open_job_desc = textAnswers[q.id] || undefined;
        }
        if (q.question_number === "OE2") {
          base.open_ai_impact = textAnswers[q.id] || undefined;
        }
        return base;
      });
    onSubmit(allAnswers);
  };

  const progress = (currentModIndex / totalMods) * 100;
  const isLastModule = currentModIndex === totalMods - 1;
  const canAdvance = !isModuleRequired || isModuleComplete;

  return (
    <div className="min-h-screen max-w-2xl mx-auto px-8 py-12">
      {/* Progress */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-3">
          <span className="text-sm text-[#64748b]">
            模块 {currentModIndex + 1} / {totalMods}
          </span>
          <span className="text-sm text-[#64748b]">
            {Math.round(progress)}%
          </span>
        </div>
        <div className="h-2 bg-[#1e293b] rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500 ease-out"
            style={{
              width: `${progress}%`,
              background: "linear-gradient(90deg, #3b82f6, #8b5cf6)",
            }}
          />
        </div>
      </div>

      {/* Module Tabs */}
      <div className="flex gap-1.5 mb-8 overflow-x-auto pb-2">
        {ALL_MODULES.map((mod, idx) => {
          const cfg = MODULE_CONFIG[mod];
          const isActive = idx === currentModIndex;
          const isPast = idx < currentModIndex;
          return (
            <button
              key={mod}
              onClick={() => setCurrentModIndex(idx)}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                isActive
                  ? "bg-[#3b82f6] text-white"
                  : isPast
                    ? "bg-[#1e293b] text-[#94a3b8] border border-[#334155]"
                    : "bg-transparent text-[#475569] border border-[#334155]"
              }`}
            >
              {mod === "V" ? "校验" : mod + ". " + cfg.label.slice(0, 5)}
            </button>
          );
        })}
      </div>

      {/* Module Header */}
      <div className="mb-8 text-center">
        <h3 className="font-[family-name:var(--font-heading)] text-2xl font-bold text-[#f1f5f9] mb-2">
          {config.label}
        </h3>
        <p className="text-[#94a3b8]">{config.description}</p>
        {isModuleRequired && (
          <span className="inline-block mt-2 text-xs px-3 py-1 rounded-full bg-[#3b82f6]/10 border border-[#3b82f6]/30 text-[#60a5fa]">
            必答模块 — 请完成所有题目
          </span>
        )}
        {currentMod === "V" && (
          <span className="inline-block mt-2 text-xs px-3 py-1 rounded-full bg-[#1e293b] border border-[#475569] text-[#64748b]">
            用于数据一致性校准，不计入评分
          </span>
        )}
      </div>

      {/* Questions */}
      <div className="space-y-6">
        {modQuestions.map((q) => (
          <div
            key={q.id}
            className="p-5 rounded-xl bg-[#1e293b] border border-[#334155]"
            style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.4)" }}
          >
            <div className="flex items-start gap-2 mb-4">
              <p className="font-medium text-[#f1f5f9] leading-relaxed flex-1">
                {q.question_text}
              </p>
              {q.input_type === "multi" && (
                <span className="shrink-0 text-xs px-2 py-0.5 rounded bg-[#8b5cf6]/10 text-[#a78bfa] border border-[#8b5cf6]/20">
                  可多选
                </span>
              )}
              {q.input_type === "text" && (
                <span className="shrink-0 text-xs px-2 py-0.5 rounded bg-[#475569]/20 text-[#94a3b8] border border-[#475569]/20">
                  选填
                </span>
              )}
            </div>
            {q.input_type === "text" ? (
              <textarea
                value={textAnswers[q.id] || ""}
                onChange={(e) => handleTextChange(q.id, e.target.value)}
                placeholder="输入你的回答（可选）..."
                rows={3}
                className="w-full px-4 py-3 rounded-lg border border-[#334155] bg-[#0f172a] text-[#cbd5e1] placeholder-[#475569] text-sm resize-none focus:outline-none focus:border-[#3b82f6] transition-colors"
              />
            ) : (
              <div className="space-y-2.5">
                {q.options.map((opt) => {
                  const selected = answers[q.id] || [];
                  const isSelected = selected.includes(opt.label);
                  return (
                    <button
                      key={opt.label}
                      onClick={() =>
                        q.input_type === "multi"
                          ? handleMultiToggle(q.id, opt.label)
                          : handleSelect(q.id, opt.label)
                      }
                      className={`w-full text-left px-4 py-3 rounded-lg border transition-all ${
                        isSelected
                          ? "border-[#3b82f6] bg-[#3b82f6]/10 text-[#f1f5f9]"
                          : "border-[#334155] text-[#94a3b8] hover:border-[#475569] hover:bg-[#0f172a]"
                      }`}
                    >
                      <span
                        className={`inline-flex items-center justify-center min-w-[28px] h-7 rounded-full text-xs font-bold mr-3 ${
                          isSelected
                            ? "bg-[#3b82f6] text-white"
                            : "bg-[#334155] text-[#94a3b8]"
                        }`}
                      >
                        {q.input_type === "multi"
                          ? (isSelected ? "x" : " ")
                          : opt.label}
                      </span>
                      {opt.text}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center mt-10">
        {currentModIndex > 0 ? (
          <button
            onClick={handlePrev}
            className="px-5 py-2.5 rounded-full border border-[#475569] text-[#cbd5e1] text-sm font-medium hover:bg-[#1e293b] transition-colors"
          >
            上一模块
          </button>
        ) : (
          <div />
        )}
        {!isLastModule ? (
          <button
            onClick={handleNext}
            disabled={!canAdvance}
            className="px-6 py-2.5 rounded-full bg-[#3b82f6] text-white text-sm font-semibold hover:bg-[#2563eb] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            style={{ boxShadow: "0 0 24px -6px rgba(59,130,246,0.3)" }}
          >
            下一模块
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={isModuleRequired && !isModuleComplete}
            className="px-8 py-2.5 rounded-full text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            style={{
              background: "linear-gradient(135deg, #16a34a, #4ade80)",
              color: "#052e16",
              boxShadow: "0 0 24px -6px rgba(22,163,74,0.4)",
            }}
          >
            查看我的职业发展报告
          </button>
        )}
      </div>
    </div>
  );
}